import { useState, useCallback, useRef } from 'react';
import { CommentThread, Comment, CommentSelection, CommentContextMenuPosition } from '@INTERFACES/comments';

interface UseCommentsOptions {
    documentId?: string;
    sectionId?: string;
    variantId?: string;
}

export function useComments(options: UseCommentsOptions = {}) {
    const [threads, setThreads] = useState<CommentThread[]>([]);
    const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
    const [contextMenu, setContextMenu] = useState<CommentContextMenuPosition>({
        x: 0,
        y: 0,
        visible: false,
    });

    const editorRef = useRef<any>(null);

    // Generate unique IDs
    const generateId = useCallback(() => Date.now().toString(36) + Math.random().toString(36).substr(2), []);

    // Get current user (mock for prototype)
    const getCurrentUser = useCallback(() => ({
        id: 'user-1',
        name: 'Current User',
        email: 'user@example.com',
    }), []);

    // Create a new comment thread from selection
    const createThread = useCallback((selection: CommentSelection, commentText: string) => {
        const threadId = generateId();
        const commentId = generateId();

        const newComment: Comment = {
            id: commentId,
            text: commentText,
            author: getCurrentUser(),
            createdAt: new Date(),
        };

        const newThread: CommentThread = {
            id: threadId,
            documentId: options.documentId,
            sectionId: options.sectionId,
            variantId: options.variantId,
            selectedText: selection.text,
            startOffset: selection.range.startOffset,
            endOffset: selection.range.endOffset,
            elementPath: '', // Will be populated by editor integration
            comments: [newComment],
            status: 'open',
            createdAt: new Date(),
            updatedAt: new Date(),
        };

        setThreads(prev => [...prev, newThread]);
        setActiveThreadId(threadId);

        return newThread;
    }, [options, generateId, getCurrentUser]);

    // Add comment to existing thread
    const addComment = useCallback((threadId: string, commentText: string) => {
        const commentId = generateId();

        const newComment: Comment = {
            id: commentId,
            text: commentText,
            author: getCurrentUser(),
            createdAt: new Date(),
        };

        setThreads(prev => prev.map(thread => (thread.id === threadId
            ? {
                ...thread,
                comments: [...thread.comments, newComment],
                updatedAt: new Date(),
            }
            : thread)));
    }, [generateId, getCurrentUser]);

    // Resolve/unresolve thread
    const toggleThreadStatus = useCallback((threadId: string) => {
        setThreads(prev => prev.map(thread => (thread.id === threadId
            ? {
                ...thread,
                status: thread.status === 'open' ? 'resolved' : 'open',
                updatedAt: new Date(),
            }
            : thread)));
    }, []);

    // Remove comment highlight from editor
    const removeCommentHighlight = useCallback((threadId: string) => {
        if (!editorRef.current) return;

        const body = editorRef.current.getBody();
        if (!body) return;

        const commentSpan = body.querySelector(`[data-comment-id="${threadId}"]`);
        if (commentSpan) {
            // Replace the span with its text content
            const textNode = document.createTextNode(commentSpan.textContent || '');
            commentSpan.parentNode?.replaceChild(textNode, commentSpan);
            
            // Trigger editor change to update content
            editorRef.current.nodeChanged();
        }
    }, []);

    // Delete thread
    const deleteThread = useCallback((threadId: string) => {
        // Remove comment annotation from editor
        removeCommentHighlight(threadId);
        
        setThreads(prev => prev.filter(thread => thread.id !== threadId));
        if (activeThreadId === threadId) {
            setActiveThreadId(null);
        }
    }, [activeThreadId, removeCommentHighlight]);

    // Handle text selection in editor
    const handleSelection = useCallback((editor: any) => {
        console.log('handleSelection called');
        
        const { selection } = editor;
        const selectedText = selection.getContent({ format: 'text' }).trim();
        
        console.log('Selected text:', selectedText);

        if (selectedText.length === 0) {
            console.log('No text selected, hiding context menu');
            setContextMenu(prev => ({ ...prev, visible: false }));
            return;
        }
        
        console.log('Text selected, showing context menu...');

        // Get selection range
        const range = selection.getRng();
        const boundingRect = range.getBoundingClientRect();

        // Skip if boundingRect is invalid (happens during rapid selections)
        if (boundingRect.width === 0 && boundingRect.height === 0) {
            return;
        }

        const commentSelection: CommentSelection = {
            text: selectedText,
            range: {
                startOffset: range.startOffset,
                endOffset: range.endOffset,
                startContainer: range.startContainer,
                endContainer: range.endContainer,
            },
            boundingRect,
        };

        // Calculate better positioning
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const menuWidth = 300; // Approximate width of context menu
        const menuHeight = 200; // Approximate height of context menu

        let x = boundingRect.right + 10;
        let y = boundingRect.top;

        // Adjust if menu would go off-screen horizontally
        if (x + menuWidth > viewportWidth) {
            x = boundingRect.left - menuWidth - 10;
            if (x < 0) {
                x = Math.max(10, viewportWidth - menuWidth - 10);
            }
        }

        // Adjust if menu would go off-screen vertically
        if (y + menuHeight > viewportHeight) {
            y = Math.max(10, boundingRect.bottom - menuHeight);
        }

        // Ensure minimum distance from edges
        x = Math.max(10, Math.min(x, viewportWidth - menuWidth - 10));
        y = Math.max(10, Math.min(y, viewportHeight - menuHeight - 10));

        // Position context menu
        console.log('Setting context menu visible at position:', { x, y });
        setContextMenu({
            x,
            y,
            visible: true,
            selection: commentSelection,
        });
    }, []);

    // Hide context menu
    const hideContextMenu = useCallback(() => {
        setContextMenu(prev => ({ ...prev, visible: false }));
    }, []);

    // Wrap selected text with comment annotation
    const wrapSelectionWithComment = useCallback((threadId: string, selection: CommentSelection) => {
        if (!editorRef.current) return;

        const editor = editorRef.current;
        const range = editor.selection.getRng();

        // Create comment annotation span
        const span = editor.getDoc().createElement('span');
        span.setAttribute('data-comment-id', threadId);
        span.setAttribute('class', 'comment-annotation newly-created');
        span.setAttribute('title', `Comment: ${selection.text.substring(0, 50)}...`);

        try {
            // Wrap the selection with the span
            range.surroundContents(span);
            editor.selection.setRng(range);
            editor.nodeChanged();
            
            // Remove newly-created class after animation
            setTimeout(() => {
                span.classList.remove('newly-created');
            }, 1500);
        } catch (error) {
            console.warn('Could not wrap selection:', error);
            // Fallback: insert the span with selected content
            const content = range.extractContents();
            span.appendChild(content);
            range.insertNode(span);
            
            // Remove newly-created class after animation
            setTimeout(() => {
                span.classList.remove('newly-created');
            }, 1500);
        }
    }, []);

    // Update visual states of comment annotations (active/inactive)
    const updateCommentAnnotationStates = useCallback((threadId: string | null) => {
        if (!editorRef.current) return;

        const body = editorRef.current.getBody();
        if (!body) return;

        // Remove active class from all annotations
        const allAnnotations = body.querySelectorAll('.comment-annotation');
        allAnnotations.forEach((annotation: HTMLElement) => {
            annotation.classList.remove('active');
        });

        // Add active class to the selected annotation
        if (threadId) {
            const activeAnnotation = body.querySelector(`[data-comment-id="${threadId}"]`);
            if (activeAnnotation) {
                activeAnnotation.classList.add('active');
            }
        }
    }, []);

    // Click handler for comment annotations
    const handleAnnotationClick = useCallback((threadId: string) => {
        setActiveThreadId(threadId);
        
        // Update visual state of comment annotations
        updateCommentAnnotationStates(threadId);
    }, [updateCommentAnnotationStates]);

    // Restore interactivity for existing comment annotations in editor content
    const restoreCommentHighlights = useCallback((editor: any) => {
        if (!editor) return;

        const body = editor.getBody();
        if (!body) return;

        // Find all existing comment annotation spans
        const commentSpans = body.querySelectorAll('.comment-annotation[data-comment-id]');
        
        commentSpans.forEach((span: HTMLElement) => {
            const threadId = span.getAttribute('data-comment-id');
            if (threadId) {
                // Add click handler for existing annotations
                span.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAnnotationClick(threadId);
                });

                // Ensure proper styling is applied
                span.classList.add('comment-annotation');
                
                // Add tooltip if not present
                if (!span.getAttribute('title')) {
                    const existingThread = threads.find(t => t.id === threadId);
                    if (existingThread) {
                        span.setAttribute('title', `Comment: ${existingThread.selectedText.substring(0, 50)}...`);
                    }
                }
            }
        });
    }, [threads, handleAnnotationClick]);

    // Set editor reference
    const setEditorRef = useCallback((editor: any) => {
        editorRef.current = editor;
        
        // Scan existing content for comment annotations and restore highlight interactivity
        restoreCommentHighlights(editor);
    }, [restoreCommentHighlights]);

    // Clear active thread
    const clearActiveThread = useCallback(() => {
        setActiveThreadId(null);
        updateCommentAnnotationStates(null);
    }, [updateCommentAnnotationStates]);

    return {
        threads,
        activeThreadId,
        contextMenu,
        createThread,
        addComment,
        toggleThreadStatus,
        deleteThread,
        handleSelection,
        hideContextMenu,
        wrapSelectionWithComment,
        handleAnnotationClick,
        setActiveThreadId,
        setEditorRef,
        restoreCommentHighlights,
        updateCommentAnnotationStates,
        clearActiveThread,
    };
} 